import {
  Activity,
  EngagementAssignedOutdoorActivityResult,
  MonitoringSpecialistAssignmentOutdoorActivityType,
  MonitoringSpecialistDto
} from '../api/models'
import { UNKNOWN_STATE } from '../pages/engagement-review/ErtPage/EngagementDetailsEnums'

export interface SpecialistIdentity {
  monitoringServiceProviderId: string
  specialistId: string
}

function getSpecialistActorIfSpecialistAssignmentActivity(activity: Activity): SpecialistIdentity | undefined {
  if (!activity || activity.type !== MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment) {
    return undefined
  }

  const actor = getMonitoringSpecialistActor(activity)
  if (actor) {
    return actor
  }
}

export function getResolvedSpecialistIdentities(activities: Activity[]): SpecialistIdentity[] {
  const seen = new Set<string>()

  return activities.flatMap(activity => {
    if (activity.type !== MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment) {
      return []
    }

    const actor = getSpecialistActorIfSpecialistAssignmentActivity(activity)
    if (!actor?.monitoringServiceProviderId || !actor.specialistId) {
      return []
    }

    const key = `${actor.monitoringServiceProviderId}:${actor.specialistId}`
    if (seen.has(key)) {
      return []
    }

    seen.add(key)
    return [{ monitoringServiceProviderId: actor.monitoringServiceProviderId, specialistId: actor.specialistId }]
  })
}

export function getMonitoringSpecialistActor(activity: Activity): SpecialistIdentity | undefined {
  const startedActor = activity.started?.actor

  if (startedActor?.monitoringServiceProviderId && startedActor?.id) {
    return {
      monitoringServiceProviderId: startedActor.monitoringServiceProviderId,
      specialistId: startedActor.id
    }
  }

  const completedActor = (activity.completed?.result as EngagementAssignedOutdoorActivityResult | undefined)?.monitoringSpecialistActor

  if (completedActor) {
    return {
      monitoringServiceProviderId: completedActor.monitoringServiceProviderId,
      specialistId: completedActor.id
    }
  }
}

export function getSpecialistNames(
  specialists: Array<Partial<Pick<MonitoringSpecialistDto, 'firstName' | 'lastName'>> | null | undefined>
): string[] {
  const names = specialists.flatMap(specialist => {
    if (!specialist?.firstName || !specialist?.lastName) {
      return []
    }

    return [`${specialist.firstName} ${specialist.lastName}`]
  })

  return names.length > 0 ? Array.from(new Set(names)) : [UNKNOWN_STATE]
}

export function getSpecialistLabel(specialistNames: string[]): string {
  return specialistNames.length > 1 ? 'Specialists' : 'Specialist'
}
